import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQueries } from "@tanstack/react-query";
import { TimelineItem } from "@/models/legislation/Timeline";
import { InboundChange } from "@/models/legislation/metadata/ConsolidationMetadata";
import { ConsolidationRenderResult, ConsolidationView } from "@/models/consolidation";
import RestResponse, { restError } from "@/models/client/RestResponse";
import { PATH_KEYS } from "@/services/appServices";
import {
  processConsolidatedHtml,
  processLegislationHtml,
} from "./useLegislationContent";
import { computeHtmlDiff } from "@/lib/htmlDiff";
import { useItemService } from "./useServices";
import { DataSource } from "@/config/datasources";
import { getTimelineRangeBetween, flattenTimeline } from "@/lib/diffViewHtml";
import {
  buildTimelineCompareSnapshots,
  isCompareSnapshotSettled,
  resolveCompareSnapshotHtml,
  timelineItemToCompareTargetInfo,
  validateTimelineComparisonRange,
} from "@/lib/consolidationComparisonUtils";
import { CONSOLIDATION_NOT_FOUND_MESSAGE } from "@/lib/consolidationMetadata";
import { ConsolidationServiceType } from "@/services/consolidationService";
import { logger } from "@/lib/logger";
import { isCancellation } from "@/lib/abort";

const RETSINFORMATION = DataSource.Retsinformation;

/** Snapshot of version info used both for the compare target and per-step labels. */
export interface CompareTargetInfo {
  accessionNumber: string;
  title: string;
  shortName: string;
  year: number;
  signatureDate: string;
}

/** One consecutive-version diff produced by the stepper. */
export interface ComparisonStep {
  before: CompareTargetInfo;
  after: CompareTargetInfo;
  diffHtml: string | null;
  error: string | null;
}

export interface UseLegislationComparisonResult {
  isCompareMode: boolean;
  compareTargetInfo: CompareTargetInfo | null;
  activeStepBeforeInfo: CompareTargetInfo | null;
  activeStepAfterInfo: CompareTargetInfo | null;
  diffHtml: string | null;
  isComputing: boolean;
  fetchedCount: number;
  totalToFetch: number;
  diffError: string | null;
  steps: ComparisonStep[];
  currentStepIndex: number;
  totalSteps: number;
  toggleCompareMode: () => void;
  exitCompareMode: () => void;
  selectCompareTarget: (item: TimelineItem) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  activeStepBeforeHtml: string | null;
  activeStepAfterHtml: string | null;
}

export type ConsolidationComparisonOptions = {
  anchorLawExternalId: string;
  inboundChanges: InboundChange[];
  view: ConsolidationView;
  isConsolidatedRender: boolean;
};

interface UseLegislationComparisonOptions {
  currentProcessedHtml: string;
  currentExternalId: string | undefined;
  datasource: string | undefined;
  timelineItems: TimelineItem[] | undefined;
  /** Mixed LBK silver + consolidation API fetches along the timeline range */
  consolidationComparison?: ConsolidationComparisonOptions | null;
}

const unwrapConsolidationResponse = async (
  responsePromise: RestResponse<ConsolidationRenderResult>
): Promise<ConsolidationRenderResult> => {
  const response = await responsePromise;
  if (response.success) return response.value;
  if (response.status === 404) {
    throw restError(response, CONSOLIDATION_NOT_FOUND_MESSAGE);
  }
  throw restError(response);
};

const fetchConsolidationCompareHtml = async (
  consolidationService: ConsolidationServiceType,
  anchorLawExternalId: string,
  changeLaw: string,
  view: ConsolidationView,
  signal: AbortSignal
): Promise<string> => {
  const result = await unwrapConsolidationResponse(
    consolidationService.renderByChangeLaw(
      {
        lawExternalId: anchorLawExternalId,
        changeLaw,
        view,
      },
      signal
    )
  );
  if (!result.html?.trim()) {
    throw new Error(
      "Den valgte version har ingen tilgængelig konsolideret tekst."
    );
  }
  return result.html;
};

const compareTargetFromTimeline = (
  item: TimelineItem,
  flatItems: TimelineItem[]
): CompareTargetInfo => {
  const accession = item.accessionNumber ?? "";
  const match =
    flatItems.find((entry) => entry.accessionNumber === accession) ?? item;
  return timelineItemToCompareTargetInfo(match, accession);
};

/**
 * Manages legislation comparison (M-23) with multi-step support along an
 * anchor LBK window using mixed silver LBK / changelaw consolidation renders.
 */
const useLegislationComparison = ({
  currentProcessedHtml,
  currentExternalId,
  datasource,
  timelineItems,
  consolidationComparison = null,
}: UseLegislationComparisonOptions): UseLegislationComparisonResult => {
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [compareTargetInfo, setCompareTargetInfo] =
    useState<CompareTargetInfo | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepDiffResults, setStepDiffResults] = useState<
    Record<number, { diffHtml: string | null; error: string | null }>
  >({});
  const [stepHtmlSnapshots, setStepHtmlSnapshots] = useState<
    Record<number, { before: string; after: string }>
  >({});
  const [rangeValidationError, setRangeValidationError] = useState<string | null>(
    null
  );
  const computingStepsRef = useRef(new Set<number>());

  const consolidationService = useItemService(
    (services) => services.consolidation
  );
  const legislationService = useItemService((services) => services.legislation);

  const flatItems = useMemo(
    () => (timelineItems ? flattenTimeline(timelineItems) : []),
    [timelineItems]
  );

  const rangeItems = useMemo<TimelineItem[] | null>(() => {
    const targetAccession = compareTargetInfo?.accessionNumber;
    if (!targetAccession || !currentExternalId) return null;

    return getTimelineRangeBetween(
      flatItems,
      targetAccession,
      currentExternalId
    );
  }, [compareTargetInfo?.accessionNumber, currentExternalId, flatItems]);

  const compareSnapshots = useMemo(() => {
    if (!consolidationComparison || !rangeItems) return [];
    return buildTimelineCompareSnapshots(rangeItems, {
      inboundChanges: consolidationComparison.inboundChanges,
      currentExternalId,
      isConsolidatedRender: consolidationComparison.isConsolidatedRender,
    });
  }, [consolidationComparison, currentExternalId, rangeItems]);

  const hasValidRange =
    Boolean(consolidationComparison) &&
    compareSnapshots.length >= 2 &&
    validateTimelineComparisonRange(
      rangeItems ?? [],
      consolidationComparison!.inboundChanges
    ) === null;

  useEffect(() => {
    setStepDiffResults({});
    setStepHtmlSnapshots({});
    computingStepsRef.current.clear();
    setCurrentStepIndex(0);
  }, [consolidationComparison?.view]);

  useEffect(() => {
    setStepDiffResults({});
    setStepHtmlSnapshots({});
    computingStepsRef.current.clear();
  }, [currentProcessedHtml]);

  useEffect(() => {
    if (!consolidationComparison) {
      setRangeValidationError(null);
      return;
    }
    if (!compareTargetInfo?.accessionNumber) return;
    if (!rangeItems) {
      setRangeValidationError(
        "Den valgte version kan ikke sammenlignes med den aktuelle."
      );
      return;
    }
    const validationError = validateTimelineComparisonRange(
      rangeItems,
      consolidationComparison.inboundChanges
    );
    setRangeValidationError(validationError);
  }, [
    compareTargetInfo?.accessionNumber,
    consolidationComparison,
    rangeItems,
  ]);

  const mixedTimelineQueries = useQueries({
    queries: compareSnapshots.map((snapshot, index) => {
      const anchorLawExternalId =
        consolidationComparison?.anchorLawExternalId ?? "";
      const view = consolidationComparison?.view ?? "draft";

      if (snapshot.fetch.kind === "embedded") {
        return {
          queryKey: [PATH_KEYS.consolidation, "compare-embedded", index],
          queryFn: async () => null as string | null,
          enabled: false,
        };
      }

      if (snapshot.fetch.kind === "consolidation") {
        const changeLaw = snapshot.fetch.changeLaw;
        return {
          queryKey: [
            PATH_KEYS.consolidation,
            "compare-render",
            anchorLawExternalId,
            changeLaw,
            view,
          ],
          queryFn: async ({ signal }: { signal: AbortSignal }) => {
            try {
              const html = await fetchConsolidationCompareHtml(
                consolidationService,
                anchorLawExternalId,
                changeLaw,
                view,
                signal
              );
              return processConsolidatedHtml(html, datasource);
            } catch (error) {
              // Leaving compare mode or changing the range cancels the
              // query; that is not a failed render.
              if (!isCancellation(error, signal)) {
                logger.warn("Consolidation compare render failed", {
                  operation: "fetchConsolidationCompareHtml",
                  error,
                  lawExternalId: anchorLawExternalId,
                  changeLaw,
                  view,
                });
              }
              throw error;
            }
          },
          enabled:
            Boolean(consolidationComparison) &&
            isCompareMode &&
            hasValidRange &&
            Boolean(anchorLawExternalId),
          staleTime: 5 * 60 * 1000,
          retry: (failureCount, error) => {
            if (
              error instanceof Error &&
              error.message === CONSOLIDATION_NOT_FOUND_MESSAGE
            ) {
              return false;
            }
            return failureCount < 2;
          },
        };
      }

      return {
        queryKey: [
          PATH_KEYS.legislation,
          "compare-silver",
          snapshot.accessionNumber,
          RETSINFORMATION,
        ],
        queryFn: async () => {
          const response = await legislationService.getByExternalId(
            snapshot.accessionNumber,
            RETSINFORMATION
          );
          if (!response.success) {
            throw restError(response);
          }
          const raw =
            response.value.htmlWithIds ?? response.value.html ?? "";
          if (!raw.trim()) {
            throw new Error("Den valgte version har ingen tilgængelig HTML.");
          }
          return processLegislationHtml(raw, RETSINFORMATION);
        },
        enabled:
          Boolean(consolidationComparison) &&
          isCompareMode &&
          hasValidRange &&
          Boolean(snapshot.accessionNumber),
        staleTime: 5 * 60 * 1000,
      };
    }),
  });

  const compareQueryStates = useMemo(
    () =>
      mixedTimelineQueries.map((query) => ({
        data: query.data,
        isError: query.isError,
        error: query.error,
      })),
    [mixedTimelineQueries]
  );

  const resolveSnapshotHtml = useCallback(
    (index: number): string | null =>
      resolveCompareSnapshotHtml(
        index,
        compareSnapshots,
        compareQueryStates,
        currentProcessedHtml
      ),
    [compareQueryStates, compareSnapshots, currentProcessedHtml]
  );

  const fetchableSnapshotCount = useMemo(
    () =>
      compareSnapshots.filter((snapshot) => snapshot.fetch.kind !== "embedded")
        .length,
    [compareSnapshots]
  );

  const fetchedCount = hasValidRange
    ? compareSnapshots.filter(
        (snapshot, index) =>
          snapshot.fetch.kind !== "embedded" &&
          isCompareSnapshotSettled(snapshot, compareQueryStates[index]) &&
          resolveCompareSnapshotHtml(
            index,
            compareSnapshots,
            compareQueryStates,
            currentProcessedHtml
          ) !== null
      ).length
    : 0;

  const totalToFetch = hasValidRange ? fetchableSnapshotCount : 0;
  const allFetched =
    compareSnapshots.length >= 2 &&
    compareSnapshots.every((snapshot, index) => {
      if (!isCompareSnapshotSettled(snapshot, compareQueryStates[index])) {
        return false;
      }
      return (
        resolveCompareSnapshotHtml(
          index,
          compareSnapshots,
          compareQueryStates,
          currentProcessedHtml
        ) !== null
      );
    });

  const steps = useMemo<ComparisonStep[]>(() => {
    if (compareSnapshots.length < 2) return [];
    return compareSnapshots.slice(0, -1).map((beforeSnapshot, index) => {
      const afterSnapshot = compareSnapshots[index + 1];
      const beforeItem =
        flatItems.find(
          (entry) => entry.accessionNumber === beforeSnapshot.accessionNumber
        ) ?? ({ accessionNumber: beforeSnapshot.accessionNumber } as TimelineItem);
      const afterItem =
        flatItems.find(
          (entry) => entry.accessionNumber === afterSnapshot.accessionNumber
        ) ?? ({ accessionNumber: afterSnapshot.accessionNumber } as TimelineItem);
      const result = stepDiffResults[index] ?? null;
      return {
        before: compareTargetFromTimeline(beforeItem, flatItems),
        after: compareTargetFromTimeline(afterItem, flatItems),
        diffHtml: result?.diffHtml ?? null,
        error: result?.error ?? null,
      };
    });
  }, [compareSnapshots, flatItems, stepDiffResults]);

  const stepCount = compareSnapshots.length - 1;

  const versionQueriesFingerprint = useMemo(
    () =>
      mixedTimelineQueries
        .map((query, index) => {
          if (compareSnapshots[index]?.fetch.kind === "embedded") {
            return "embedded";
          }
          return `${query.status}:${query.fetchStatus}:${query.isError}:${Boolean(query.data)}`;
        })
        .join("|"),
    [compareSnapshots, mixedTimelineQueries]
  );

  const recordStepFetchError = (
    stepIdx: number,
    queryError: unknown,
    fallbackMessage: string
  ) => {
    setStepDiffResults((prev) => ({
      ...prev,
      [stepIdx]: {
        diffHtml: null,
        error:
          queryError instanceof Error ? queryError.message : fallbackMessage,
      },
    }));
  };

  useEffect(() => {
    if (!consolidationComparison) return;
    if (!hasValidRange || stepCount < 1) return;
    if (!allFetched) return;

    for (let stepIdx = 0; stepIdx < stepCount; stepIdx += 1) {
      if (stepDiffResults[stepIdx] !== undefined) continue;
      if (computingStepsRef.current.has(stepIdx)) continue;

      const beforeProcessedHtml = resolveSnapshotHtml(stepIdx);
      if (!beforeProcessedHtml && mixedTimelineQueries[stepIdx]?.isError) {
        recordStepFetchError(
          stepIdx,
          mixedTimelineQueries[stepIdx]?.error,
          "Kunne ikke hente version til sammenligning."
        );
        continue;
      }
      if (!beforeProcessedHtml) continue;

      const afterProcessedHtml = resolveSnapshotHtml(stepIdx + 1);
      if (!afterProcessedHtml && mixedTimelineQueries[stepIdx + 1]?.isError) {
        recordStepFetchError(
          stepIdx,
          mixedTimelineQueries[stepIdx + 1]?.error,
          "Kunne ikke hente version til sammenligning."
        );
        continue;
      }
      if (!afterProcessedHtml) continue;

      computingStepsRef.current.add(stepIdx);

      computeHtmlDiff(beforeProcessedHtml, afterProcessedHtml)
        .then((diffHtml) => {
          setStepHtmlSnapshots((prev) => ({
            ...prev,
            [stepIdx]: { before: beforeProcessedHtml, after: afterProcessedHtml },
          }));
          setStepDiffResults((prev) => ({
            ...prev,
            [stepIdx]: { diffHtml, error: null },
          }));
        })
        .catch((err) => {
          logger.error("Failed to compute the legislation comparison diff", {
            operation: "computeComparisonStepDiff",
            error: err,
            stepIndex: stepIdx,
            fromAccession: compareSnapshots[stepIdx]?.accessionNumber,
            toAccession: compareSnapshots[stepIdx + 1]?.accessionNumber,
          });
          setStepDiffResults((prev) => ({
            ...prev,
            [stepIdx]: {
              diffHtml: null,
              error:
                err instanceof Error
                  ? err.message
                  : "Diff-beregning mislykkedes",
            },
          }));
        })
        .finally(() => {
          computingStepsRef.current.delete(stepIdx);
        });
    }
  }, [
    allFetched,
    compareSnapshots,
    consolidationComparison,
    hasValidRange,
    mixedTimelineQueries,
    resolveSnapshotHtml,
    stepCount,
    stepDiffResults,
    versionQueriesFingerprint,
  ]);

  const isComputing =
    Boolean(consolidationComparison) &&
    hasValidRange &&
    (mixedTimelineQueries.some(
      (query, index) =>
        compareSnapshots[index]?.fetch.kind !== "embedded" && query.isFetching
    ) ||
      computingStepsRef.current.size > 0 ||
      (steps.length > 0 &&
        steps.some((step) => step.diffHtml === null && step.error === null)));

  const activeStep = steps[currentStepIndex] ?? null;

  const diffHtml = activeStep?.diffHtml ?? null;
  const diffError = rangeValidationError ?? activeStep?.error ?? null;

  const activeStepBeforeInfo = activeStep?.before ?? null;
  const activeStepAfterInfo = activeStep?.after ?? null;

  const activeStepBeforeHtml = useMemo(
    () => stepHtmlSnapshots[currentStepIndex]?.before ?? null,
    [currentStepIndex, stepHtmlSnapshots]
  );

  const activeStepAfterHtml = useMemo(
    () => stepHtmlSnapshots[currentStepIndex]?.after ?? null,
    [currentStepIndex, stepHtmlSnapshots]
  );

  const toggleCompareMode = useCallback(() => {
    setIsCompareMode((prev) => {
      if (prev) {
        setCompareTargetInfo(null);
        setStepDiffResults({});
        setStepHtmlSnapshots({});
        computingStepsRef.current.clear();
        setCurrentStepIndex(0);
        setRangeValidationError(null);
      }
      return !prev;
    });
  }, []);

  const exitCompareMode = useCallback(() => {
    setIsCompareMode(false);
    setCompareTargetInfo(null);
    setStepDiffResults({});
    setStepHtmlSnapshots({});
    computingStepsRef.current.clear();
    setCurrentStepIndex(0);
    setRangeValidationError(null);
  }, []);

  const selectCompareTarget = useCallback(
    (item: TimelineItem) => {
      if (!item.accessionNumber) return;
      setStepDiffResults({});
      setStepHtmlSnapshots({});
      computingStepsRef.current.clear();
      setCurrentStepIndex(0);
      setRangeValidationError(null);
      setCompareTargetInfo(compareTargetFromTimeline(item, flatItems));
    },
    [flatItems]
  );

  const goToNextStep = useCallback(() => {
    setCurrentStepIndex((index) => Math.min(index + 1, steps.length - 1));
  }, [steps.length]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStepIndex((index) => Math.max(index - 1, 0));
  }, []);

  useEffect(() => {
    if (consolidationComparison) return;
    exitCompareMode();
  }, [consolidationComparison, exitCompareMode]);

  const comparisonActive = Boolean(consolidationComparison);

  return {
    isCompareMode: comparisonActive && isCompareMode,
    compareTargetInfo: comparisonActive ? compareTargetInfo : null,
    activeStepBeforeInfo: comparisonActive ? activeStepBeforeInfo : null,
    activeStepAfterInfo: comparisonActive ? activeStepAfterInfo : null,
    diffHtml: comparisonActive ? diffHtml : null,
    isComputing: comparisonActive ? isComputing : false,
    fetchedCount: comparisonActive ? fetchedCount : 0,
    totalToFetch: comparisonActive ? totalToFetch : 0,
    diffError: comparisonActive ? diffError : null,
    steps: comparisonActive ? steps : [],
    currentStepIndex: comparisonActive ? currentStepIndex : 0,
    totalSteps: comparisonActive ? steps.length : 0,
    toggleCompareMode: comparisonActive ? toggleCompareMode : exitCompareMode,
    exitCompareMode,
    selectCompareTarget: comparisonActive ? selectCompareTarget : () => undefined,
    goToNextStep: comparisonActive ? goToNextStep : () => undefined,
    goToPreviousStep: comparisonActive ? goToPreviousStep : () => undefined,
    activeStepBeforeHtml: comparisonActive ? activeStepBeforeHtml : null,
    activeStepAfterHtml: comparisonActive ? activeStepAfterHtml : null,
  };
};

export default useLegislationComparison;
